import { Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import { UseFormReturn, UseFormTrigger } from "react-hook-form";
import HeiCheckbox from "../../../../Common/components/Form/HeiCheckbox/HeiCheckbox";
import HeiDynamicField from "../../../../Common/components/Form/HeiDynamicField/HeiDynamicField";
import HeiTextField from "../../../../Common/components/Form/HeiTextField/HeiTextField";
import { ProjectFormType } from "../../../../Common/hooks/useCreateProject";
import ProjectTypeSelection from "../../components/ProjectTypeSelection/ProjectTypeSelection";
import ToolDetails from "../../components/ToolDetails/ToolDetails";
import useEnterDetails from "../../hooks/useEnterDetails";
import styles from "./EnterDetailsStep.module.scss";
interface EnterDetailsStepProps {
  form: UseFormReturn<ProjectFormType>;
}

const EnterDetailsStep = ({ form }: EnterDetailsStepProps): JSX.Element => {
  const { control } = form;
  const validationInProgressRef = useRef<string | null>(null);

  const { opcos, validateUniqueProjectName, dynamicFields, packageId } =
    useEnterDetails();

  useEffect(() => {
    form.setValue("packageId", packageId);
  }, []);

  return (
    <div className={styles.details}>
      <Typography className={styles.details__title} variant="h6">
        Project details
      </Typography>

      <HeiTextField
        control={control}
        name="projectName"
        label="Project name"
        rules={{
          required: "Project name is required",
          maxLength: {
            value: 80,
            message: "Project name cannot be longer then 80 characters",
          },
          pattern: {
            value: /^[A-Za-z0-9-][A-Za-z0-9\s-]+[A-Za-z0-9-]+$/,
            message:
              "Project name is invalid. Please don`t use preceding spaces. Use only latin characters, digits, -",
          },
          validate: {
            projectNameUnique: async (value: string) => {
              // Skip validation for empty or whitespace-only values
              if (!value || !value.trim()) {
                return undefined;
              }
              
              // Prevent multiple simultaneous validations for the same value
              if (validationInProgressRef.current === value) {
                return undefined;
              }
              
              try {
                validationInProgressRef.current = value;
                
                const isValid = await validateUniqueProjectName(value);
                return isValid ? undefined : `Project name ${value} is taken`;
              } catch (error) {
                // If validation fails due to network or other errors, don't show validation error
                return undefined;
              } finally {
                validationInProgressRef.current = null;
              }
            }
          },
        }}
      />

      <HeiCheckbox
        control={control}
        name="hideProject"
        label="Hide this project"
        tooltip="Hide this project if it should not be publicly visible within Heineken. Note: team members cannot onboard themselves on hidden projects."
      />

      <ProjectTypeSelection form={form} opcos={opcos} />

      {dynamicFields && (
        <>
          <Typography className={styles.packageDetails__title} variant="h6">
            Package details
          </Typography>

          {dynamicFields.map((df) => (
            <HeiDynamicField key={df.name} control={control} field={df} />
          ))}
        </>
      )}

      <br />

      <ToolDetails form={form} />
    </div>
  );
};

export default EnterDetailsStep;
